import { useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon } from "@hugeicons/core-free-icons"
import { formatCurrencyExact, privatize, toCents } from '@/lib/utils/revenueUtils'
import { convertUTCtoFormattedDate } from '@/lib/utils/timeUtils'
import { formatUtmLabel } from '@/lib/utils/utmLabel'
import type { RevenueTransaction } from '@/lib/utils/revenueUtils'

interface RecentTransactionsPanelProps {
  transactions: RevenueTransaction[]
  privacyMode?: boolean
}

export default function RecentTransactionsPanel({ transactions, privacyMode = false }: RecentTransactionsPanelProps) {
  const [isOpen, setIsOpen] = useState(false)

  if (transactions.length === 0) return null

  return (
    <div className="border-t border-border/30">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span>Recent attributed transactions</span>
          <Badge variant="secondary" className="text-[11px]">{transactions.length}</Badge>
        </div>
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={14}
          className={`text-muted-foreground transition-transform ${isOpen ? '' : '-rotate-90'}`}
        />
      </button>
      {isOpen && (
        <div className="px-4 pb-4">
          <div className="responsive-table-wrapper">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border/60 hover:bg-transparent">
                  <TableHead className="data-table-th">Date</TableHead>
                  <TableHead className="data-table-th">Source</TableHead>
                  <TableHead className="data-table-th">Campaign</TableHead>
                  <TableHead className="data-table-th">Landing page</TableHead>
                  <TableHead className="data-table-th text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.slice(0, 10).map((tx, idx) => {
                  const cents = toCents(tx)
                  const dateStr = (tx as any).granularity || tx.created_at
                  const utm = formatUtmLabel({
                    utm_source: tx.utm_source,
                    utm_medium: (tx as any).utm_medium,
                    utm_campaign: tx.utm_campaign,
                    utm_content: (tx as any).utm_content,
                    utm_term: (tx as any).utm_term,
                  })
                  const sourceLabel = tx.utm_source
                    ? utm.primary
                    : tx.referer_group || 'Direct'
                  return (
                    <TableRow key={idx} className="data-table-row border-b border-border hover:bg-muted/50">
                      <TableCell className="data-table-cell text-xs">
                        {dateStr ? convertUTCtoFormattedDate(dateStr) : '—'}
                      </TableCell>
                      <TableCell className="data-table-cell">
                        <Badge variant="outline" className="text-[11px] font-medium max-w-[160px] truncate">
                          {sourceLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="data-table-cell text-xs text-muted-foreground">
                        {utm.secondary ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate block max-w-[180px] cursor-help">
                                {utm.secondary}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[320px] break-all">
                              {utm.secondaryFull || tx.utm_campaign}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="data-table-cell">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs truncate block max-w-[200px] cursor-help">{tx.landing_page || '—'}</span>
                          </TooltipTrigger>
                          <TooltipContent>{tx.landing_page || 'Unknown'}</TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="data-table-cell data-table-cell-numeric font-medium text-foreground">
                        {privatize(formatCurrencyExact(cents / 100), privacyMode)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}
